import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { sortRowsByVehicleCode, sortVehiclesByVehicleCode } from './internalAuditVehicleCodeSort';
import {
  VehicleModuleHero,
  VehiclePageShell,
  VehiclePersonCell,
  VehiclePlateCell,
  vehiclePlateColumnProps,
  VehicleSelectionPreview,
  VehicleStatCard,
  VehicleStatGrid,
  getVehicleById,
} from './vehicleMaintenanceUi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';

const initialForm = {
  vehicle: '',
  accident_date: '',
  driver: '',
  insurance_file_status: '',
};

const buildAccidentFormData = (payload, reportFile, photoFile) => {
  const form = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      form.append(key, value);
    }
  });
  if (reportFile) form.append('report_file', reportFile);
  if (photoFile) form.append('photo', photoFile);
  return form;
};

const FileLink = ({ url, label }) => {
  if (!url) return <span className="text-gray-400">—</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-teal-700 underline hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </a>
  );
};

const InternalAuditAccidentsPage = () => {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [reportFile, setReportFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [filters, setFilters] = useState({ vehicle_id: '' });
  const [draftFilters, setDraftFilters] = useState({ vehicle_id: '' });
  const vehicleDropdownRef = useRef(null);

  const sortedVehicles = useMemo(() => sortVehiclesByVehicleCode(vehicles), [vehicles]);
  const sortedRows = useMemo(() => sortRowsByVehicleCode(rows), [rows]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!vehicleDropdownRef.current) return;
      if (!vehicleDropdownRef.current.contains(event.target)) setShowVehicleDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [accidentsResponse, vehiclesResponse] = await Promise.all([
        vehicleMaintenanceApi.getAccidents(activeFilters),
        vehicleMaintenanceApi.getVehicles({ is_active: 'true' }),
      ]);
      setRows(accidentsResponse?.data || []);
      setVehicles(vehiclesResponse?.data || []);
    } catch (err) {
      setError(err.message || 'Kaza kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSearch = () => {
    setFilters(draftFilters);
    setShowVehicleDropdown(false);
    loadData(draftFilters);
  };

  const handleClear = () => {
    const reset = { vehicle_id: '' };
    setDraftFilters(reset);
    setFilters(reset);
    setShowVehicleDropdown(false);
    loadData(reset);
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = sortedVehicles.find((v) => String(v.id) === String(vehicleId));
    setFormData((prev) => ({
      ...prev,
      vehicle: vehicleId,
      driver: prev.driver || vehicle?.assigned_person || '',
    }));
  };

  const openCreateModal = () => {
    setEditingRow(null);
    setSelectedRow(null);
    setReportFile(null);
    setPhotoFile(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row) return;
    setEditingRow(row);
    setReportFile(null);
    setPhotoFile(null);
    setFormData({
      vehicle: row.vehicle || '',
      accident_date: row.accident_date || '',
      driver: row.driver || '',
      insurance_file_status: row.insurance_file_status || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!row) return;
    if (!window.confirm('Kaza kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await vehicleMaintenanceApi.deleteAccident(row.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        vehicle: formData.vehicle ? Number(formData.vehicle) : null,
        accident_date: formData.accident_date || null,
        driver: formData.driver || '',
        insurance_file_status: formData.insurance_file_status || null,
      };
      const hasFiles = reportFile || photoFile;
      const body = hasFiles ? buildAccidentFormData(payload, reportFile, photoFile) : payload;

      if (editingRow?.id) {
        await vehicleMaintenanceApi.updateAccident(editingRow.id, body);
      } else {
        await vehicleMaintenanceApi.createAccident(body);
      }
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      setReportFile(null);
      setPhotoFile(null);
      setFormData(initialForm);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      header: 'Araç ID',
      accessorKey: 'vehicle_code',
      cell: ({ getValue }) => (
        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {getValue() || '—'}
        </span>
      ),
    },
    {
      ...vehiclePlateColumnProps,
      cell: ({ row }) => {
        const vehicle = getVehicleById(vehicles, row.original.vehicle);
        return (
          <VehiclePlateCell
            plate={row.original.license_plate}
            brand={vehicle?.brand}
            model={vehicle?.model}
          />
        );
      },
    },
    { header: 'Kaza Tarihi', accessorKey: 'accident_date', cell: ({ getValue }) => getValue() || '—' },
    {
      header: 'Sürücü',
      accessorKey: 'driver',
      cell: ({ row }) => <VehiclePersonCell name={row.original.driver} subtitle="Sürücü" />,
    },
    {
      header: 'Kaza Tutanağı',
      accessorKey: 'report_file_url',
      cell: ({ getValue }) => <FileLink url={getValue()} label="Tutanak" />,
    },
    {
      header: 'Fotoğraf',
      accessorKey: 'photo_url',
      cell: ({ getValue }) => <FileLink url={getValue()} label="Fotoğraf" />,
    },
    {
      header: 'Sigorta Dosya Durumu',
      accessorKey: 'insurance_file_status',
      cell: ({ getValue }) => getValue() || '—',
    },
  ], [vehicles]);

  const stats = useMemo(() => {
    const total = sortedRows.length;
    const withReport = sortedRows.filter((r) => r.report_file_url).length;
    const withPhoto = sortedRows.filter((r) => r.photo_url).length;
    const withInsuranceStatus = sortedRows.filter((r) => r.insurance_file_status).length;
    return { total, withReport, withPhoto, withInsuranceStatus };
  }, [sortedRows]);

  const selectedVehiclePreview = useMemo(() => {
    if (!selectedRow) return null;
    return getVehicleById(vehicles, selectedRow.vehicle) || {
      license_plate: selectedRow.license_plate,
      vehicle_code: selectedRow.vehicle_code,
      brand: null,
      model: null,
      assigned_person: null,
    };
  }, [selectedRow, vehicles]);

  const getRowClassName = (row) => {
    if (!row.report_file_url && !row.photo_url) return 'border-l-[3px] border-l-amber-400 dark:border-l-amber-500';
    return '';
  };

  return (
    <VehiclePageShell>
        <VehicleModuleHero
          icon={ExclamationTriangleIcon}
          title="Kaza Takip"
          subtitle="Kaza tutanağı ve fotoğraf kayıtlarını yönetin, sigorta dosya durumunu takip edin."
          accent="teal"
        />

        <VehicleStatGrid>
          <VehicleStatCard label="Toplam Kaza" value={stats.total} tone="slate" icon={ExclamationTriangleIcon} />
          <VehicleStatCard label="Tutanaklı" value={stats.withReport} tone="emerald" icon={DocumentTextIcon} />
          <VehicleStatCard label="Fotoğraflı" value={stats.withPhoto} tone="blue" icon={PhotoIcon} />
          <VehicleStatCard label="Sigorta Durumu" value={stats.withInsuranceStatus} tone="amber" icon={CheckCircleIcon} />
        </VehicleStatGrid>

        {selectedVehiclePreview ? (
          <VehicleSelectionPreview
            vehicle={selectedVehiclePreview}
            personName={selectedRow?.driver}
            personLabel="Sürücü"
          />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
          <div className="grid grid-cols-1 gap-1.5">
            <div className="rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20">
              <div className="sr-only text-teal-800 dark:text-teal-300">
                Filtreler
              </div>
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Araç</legend>
                  <div className="relative" ref={vehicleDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowVehicleDropdown((prev) => !prev)}
                      className="flex h-5 w-full items-center justify-between bg-transparent px-0 text-[10px] text-gray-900 outline-none dark:text-white"
                    >
                      <span className="truncate">
                        {(() => {
                          if (!draftFilters.vehicle_id) return 'Tümü';
                          const selected = sortedVehicles.find((v) => String(v.id) === String(draftFilters.vehicle_id));
                          return selected ? `${selected.vehicle_code} - ${selected.license_plate}` : 'Tümü';
                        })()}
                      </span>
                      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                    {showVehicleDropdown ? (
                      <div className="absolute left-0 right-0 z-50 mt-1 max-h-[196px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => {
                            setDraftFilters((prev) => ({ ...prev, vehicle_id: '' }));
                            setShowVehicleDropdown(false);
                          }}
                          className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                        >
                          Tümü
                        </button>
                        {sortedVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            type="button"
                            onClick={() => {
                              setDraftFilters((prev) => ({ ...prev, vehicle_id: String(vehicle.id) }));
                              setShowVehicleDropdown(false);
                            }}
                            className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            {vehicle.vehicle_code} - {vehicle.license_plate}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
            <FilterActions compact onClear={handleClear} onSearch={handleSearch} />
          </div>
        </FilterPanel>

        <ActionToolbar className="rounded-xl border border-teal-100/80 bg-white/80 px-2 py-1.5 shadow-sm dark:border-teal-900/40 dark:bg-gray-900/60">
          <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
          <IconActionButton icon={PencilSquareIcon} onClick={() => openEditModal(selectedRow)} title="Düzenle" disabled={!selectedRow} />
          <IconActionButton icon={TrashIcon} onClick={() => handleDelete(selectedRow)} title="Sil" disabled={!selectedRow} />
        </ActionToolbar>

        <DataTable
          data={sortedRows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          onRowClick={setSelectedRow}
          onRowDoubleClick={openEditModal}
          selectedRowId={selectedRow?.id}
          compact
          compactCellWrap
          modern
          getRowClassName={getRowClassName}
        />

      <ResponsiveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-3xl">
        <div className="px-4 pt-5 pb-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingRow ? 'Kaza Kaydını Düzenle' : 'Yeni Kaza Kaydı'}
            </h2>
            <button type="button" onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Araç</Label>
                <select
                  required
                  className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  value={formData.vehicle}
                  onChange={(e) => handleVehicleChange(e.target.value)}
                >
                  <option value="">Araç seçin</option>
                  {sortedVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicle_code} - {vehicle.license_plate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Kaza Tarihi</Label>
                <Input
                  required
                  type="date"
                  value={formData.accident_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, accident_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Sürücü</Label>
                <Input
                  required
                  value={formData.driver}
                  onChange={(e) => setFormData((prev) => ({ ...prev, driver: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Sigorta Dosya Durumu</Label>
                <Input
                  value={formData.insurance_file_status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, insurance_file_status: e.target.value }))}
                  placeholder="Örn: Dosya açıldı, eksper bekleniyor"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Kaza Tutanağı</Label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 cursor-pointer text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <DocumentArrowUpIcon className="h-5 w-5" />
                    <span>{reportFile ? reportFile.name : 'Tutanak dosyası seç'}</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,image/*"
                      onChange={(e) => setReportFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {editingRow?.report_file_url && !reportFile ? (
                    <FileLink url={editingRow.report_file_url} label="Mevcut tutanağı görüntüle" />
                  ) : null}
                </div>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Fotoğraf</Label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 cursor-pointer text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <PhotoIcon className="h-5 w-5" />
                    <span>{photoFile ? photoFile.name : 'Fotoğraf seç'}</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {editingRow?.photo_url && !photoFile ? (
                    <FileLink url={editingRow.photo_url} label="Mevcut fotoğrafı görüntüle" />
                  ) : null}
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </VehiclePageShell>
  );
};

export default InternalAuditAccidentsPage;
